import React, {useEffect, useState} from 'react';
import {Table, Button, Modal, Form, Input, Switch, message, Space, Popconfirm, Card, Tag} from 'antd';
import {PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined} from '@ant-design/icons';
import {getList, createUser, updateUser, deleteUser} from '../../../Http/request';
import {BasicDataResponse, User, UserListResponse} from '../../../Http/common';

const {Search} = Input;

export default function UserAdmin() {
    const [messageApi, contextHolder] = message.useMessage();
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchUserName, setSearchUserName] = useState('');

    const [modalVisible, setModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [isEdit, setIsEdit] = useState(false);
    const [form] = Form.useForm();

    const fetchUserList = async (page: number = 1, size: number = 10, userName: string = '') => {
        setLoading(true);
        try {
            const res: BasicDataResponse<UserListResponse> = await getList(page, size, userName);
            if (res.code === 0) {
                setUsers(res.data.data);
                setTotal(res.data.total);
            } else {
                messageApi.error('获取用户列表失败');
            }
        } catch (error) {
            messageApi.error('获取用户列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUserList(currentPage, pageSize, searchUserName);
    }, []);

    const handleSearch = (value: string) => {
        setSearchUserName(value);
        setCurrentPage(1);
        fetchUserList(1, pageSize, value);
    };

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
        fetchUserList(page, size, searchUserName);
    };

    /*处理新增一个用户*/
    const handleAdd = () => {
        setIsEdit(false);
        setEditingUser(null);
        form.resetFields();
        form.setFieldsValue({ errorTimes: 0 });
        setModalVisible(true);
    };

    /*处理编辑一个用户*/
    const handleEdit = (record: User) => {
        setIsEdit(true);
        setEditingUser(record);
        form.setFieldsValue({
            id: record.id,
            name: record.name,
            password: '',
            isAdmin: record.isAdmin,
            errorTimes: record.errorTimes
        });
        setModalVisible(true);
    };

    const handleDelete = async (id: number) => {
        try {
            const res = await deleteUser(id);
            if (res.code === 0) {
                messageApi.success('删除用户成功');
                fetchUserList(currentPage, pageSize, searchUserName);
            } else {
                messageApi.error(res.data || '删除用户失败');
            }
        } catch (error) {
            messageApi.error('删除用户失败');
        }
    };

    /*处理对话框OK*/
    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            
            if (isEdit) {
                const updateData: User = {
                    id: editingUser!.id,
                    name: values.name,
                    password: values.password || editingUser!.password,
                    isAdmin: values.isAdmin,
                    errorTimes: parseInt(values.errorTimes, 10)
                };
                const res = await updateUser(updateData);
                if (res.code === 0) {
                    messageApi.success('更新用户成功');
                } else {
                    messageApi.error(res.data || '更新用户失败');
                }
            } else {
                const createData: User = {
                    id: 0,
                    name: values.name,
                    password: values.password,
                    isAdmin: values.isAdmin || false,
                    errorTimes: 0, // 新建默认用户错误次数为0
                };
                const res = await createUser(createData);
                if (res.code === 0) {
                    messageApi.success('创建用户成功');
                } else {
                    messageApi.error(res.data || '创建用户失败');
                }
            }
            
            setModalVisible(false);
            fetchUserList(currentPage, pageSize, searchUserName);
        } catch (error) {
            console.error('表单验证失败', error);
        } finally {
            setLoading(false);
        }
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        form.resetFields();
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80
        },
        {
            title: '用户名',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: '是否管理员',
            dataIndex: 'isAdmin',
            key: 'isAdmin',
            width: 120,
            render: (isAdmin: boolean) => {
                if (isAdmin) {
                    return (
                        <Tag color="gold">
                            是
                        </Tag>
                    )
                } else {
                    return (
                        <Tag color="default">
                            否
                        </Tag>
                    )
                }
            }
        },
        {
            title: '登录错误次数',
            dataIndex: 'errorTimes',
            key: 'errorTimes',
            width: 120
        },
        {
            title: '操作',
            key: 'action',
            width: 150,
            render: (_: any, record: User) => (
                <Space size="small">
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确认删除"
                        description="确定要删除该用户吗？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <>
            {contextHolder}
            <Card style={{marginTop: '3vh'}}>
                <div style={{marginBottom: 16, display: 'flex', justifyContent: 'space-between'}}>
                    <Search
                        placeholder="按用户名搜索"
                        allowClear
                        enterButton={<SearchOutlined />}
                        style={{width: 300}}
                        onSearch={handleSearch}
                        onClear={() => handleSearch('')}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                    >
                        新增用户
                    </Button>
                </div>
                
                <Table
                    columns={columns}
                    dataSource={users}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条`,
                        onChange: handlePageChange,
                        onShowSizeChange: handlePageChange
                    }}
                />
            </Card>

            <Modal
                title={isEdit ? '编辑用户' : '新增用户'}
                open={modalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                confirmLoading={loading}
            >
                <Form
                    form={form}
                    layout="vertical"
                    autoComplete="off"
                >
                    <Form.Item
                        label="用户名"
                        name="name"
                        rules={[
                            {required: true, message: '请输入用户名'},
                            {max: 50, message: '用户名不能超过32个字符'},
                            {min: 1, message: '用户名不能为空'}
                        ]}
                    >
                        <Input disabled={isEdit} placeholder="请输入用户名" />
                    </Form.Item>

                    <Form.Item
                        label="密码"
                        name="password"
                        rules={isEdit ? [
                            {min: 16, message: '密码至少16位'},
                            {max: 128, message: '密码不能超过128个字符'}
                        ] : [
                            {required: true, message: '请输入密码'},
                            {min: 16, message: '密码至少16位'},
                            {max: 128, message: '密码不能超过128个字符'}
                        ]}
                        extra={isEdit ? '不修改密码请留空' : ''}
                    >
                        <Input.Password placeholder="请输入密码" />
                    </Form.Item>

                    <Form.Item
                        label="确认密码"
                        name="confirmPassword"
                        dependencies={['password']}
                        rules={[
                            ({getFieldValue}) => ({
                                validator(_, value) {
                                    if (!isEdit) {
                                        if (!value) {
                                            return Promise.reject(new Error('请再次输入密码'));
                                        }
                                        if (value !== getFieldValue('password')) {
                                            return Promise.reject(new Error('两次输入的密码不一致'));
                                        }
                                    } else if (value && value !== getFieldValue('password')) {
                                        return Promise.reject(new Error('两次输入的密码不一致'));
                                    }
                                    return Promise.resolve();
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="请再次输入密码" />
                    </Form.Item>

                    <Form.Item
                        label="是否管理员"
                        name="isAdmin"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        label="登录错误次数"
                        name="errorTimes"
                        rules={[
                            {required: true, message: '请输入登录错误次数'},
                            {
                                validator(_, value) {
                                    if (value === undefined || value === null || value === '') {
                                        return Promise.reject(new Error('请输入登录错误次数'));
                                    }
                                    const num = Number(value);
                                    if (isNaN(num) || num < 0) {
                                        return Promise.reject(new Error('错误次数不能为负数'));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Input type="number" placeholder="请输入登录错误次数" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}
